// File: clientapp/src/pages/SecurityGroupDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    TextField,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export default function SecurityGroupDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [groupDetail, setGroupDetail] = useState({
        groupId: null,
        name: "",
        description: "",
        users: [],   // array of user objects: { id, email }
        roles: [],   // array of role objects: { id, name }
        features: [] // array of feature objects: { name, description }
    });
    const [saveMessage, setSaveMessage] = useState("");

    // States for Group Users management
    const [userFilter, setUserFilter] = useState("");
    const [userPage, setUserPage] = useState(0);
    const [userRowsPerPage, setUserRowsPerPage] = useState(10);

    // States for Add User Dialog
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [availUserPage, setAvailUserPage] = useState(0);
    const [availUserRowsPerPage, setAvailUserRowsPerPage] = useState(10);

    // States for Group Roles management
    const [roleFilter, setRoleFilter] = useState("");
    const [rolePage, setRolePage] = useState(0);
    const [roleRowsPerPage, setRoleRowsPerPage] = useState(10);

    // States for Add Role Dialog (declared only once)
    const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [roleSearch, setRoleSearch] = useState("");
    const [availRolePage, setAvailRolePage] = useState(0);
    const [availRoleRowsPerPage, setAvailRoleRowsPerPage] = useState(10);

    useEffect(() => {
        if (!isNew) {
            fetchGroupDetail();
        }
    }, [id, isNew]);

    async function fetchGroupDetail() {
        try {
            const response = await fetch(`/api/security/groups/${id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setGroupDetail(data);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    const handleSaveGroup = async () => {
        try {
            const url = isNew ? `/api/security/groups` : `/api/security/groups/${id}`;
            const method = isNew ? 'POST' : 'PUT';
            const payload = {
                name: groupDetail.name,
                description: groupDetail.description
            };
            const response = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setSaveMessage("Group saved successfully.");
                if (isNew) {
                    const created = await response.json();
                    navigate(`/security/groups/${created.groupId}`);
                } else {
                    fetchGroupDetail();
                }
            } else {
                setSaveMessage("Failed to save group.");
            }
        } catch (err) {
            console.error(err);
            setSaveMessage("Error saving group.");
        }
    };

    // --- Group Users Management ---

    const handleRemoveUser = async (userId) => {
        if (window.confirm("Remove this user from the group?")) {
            try {
                const response = await fetch(`/api/security/groups/${id}/users/${userId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchGroupDetail();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleOpenAddUserDialog = () => {
        setAddUserDialogOpen(true);
        setAvailUserPage(0);
        setUserSearch("");
        fetchAvailableUsers("");
    };

    const handleCloseAddUserDialog = () => {
        setAddUserDialogOpen(false);
        setUserSearch("");
        setAvailableUsers([]);
        setAvailUserPage(0);
    };

    async function fetchAvailableUsers(searchTerm) {
        try {
            const response = await fetch(`/api/security/availableUsers?groupId=${id}&search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableUsers(data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleAddUser = async (userId) => {
        try {
            const response = await fetch(`/api/security/groups/${id}/users`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });
            if (response.ok) {
                fetchGroupDetail();
            }
        } catch (err) {
            console.error(err);
        }
        handleCloseAddUserDialog();
    };

    const handleUserPageChange = (event, newPage) => {
        setUserPage(newPage);
    };

    const handleUserRowsPerPageChange = (event) => {
        setUserRowsPerPage(parseInt(event.target.value, 10));
        setUserPage(0);
    };

    const paginatedUsers = groupDetail.users
        ? groupDetail.users.slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage)
        : [];

    // --- Group Roles Management ---

    const handleRemoveRole = async (roleId) => {
        if (window.confirm("Remove this role from the group?")) {
            try {
                const response = await fetch(`/api/security/groups/${id}/roles/${roleId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchGroupDetail();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleOpenAddRoleDialog = () => {
        setAddRoleDialogOpen(true);
        setAvailRolePage(0);
        setRoleSearch("");
        fetchAvailableRoles("");
    };

    const handleCloseAddRoleDialog = () => {
        setAddRoleDialogOpen(false);
        setRoleSearch("");
        setAvailableRoles([]);
        setAvailRolePage(0);
    };

    async function fetchAvailableRoles(searchTerm) {
        try {
            const response = await fetch(`/api/security/availableRoles?groupId=${id}&search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableRoles(data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleAddRole = async (roleId) => {
        try {
            const response = await fetch(`/api/security/groups/${id}/roles`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleId })
            });
            if (response.ok) {
                fetchGroupDetail();
            }
        } catch (err) {
            console.error(err);
        }
        handleCloseAddRoleDialog();
    };

    const handleRolePageChange = (event, newPage) => {
        setRolePage(newPage);
    };

    const handleRoleRowsPerPageChange = (event) => {
        setRoleRowsPerPage(parseInt(event.target.value, 10));
        setRolePage(0);
    };

    const paginatedRoles = groupDetail.roles
        ? groupDetail.roles.slice(rolePage * roleRowsPerPage, rolePage * roleRowsPerPage + roleRowsPerPage)
        : [];

    // --- Pagination for Add User Dialog ---
    const handleAvailUserPageChange = (event, newPage) => {
        setAvailUserPage(newPage);
    };

    const handleAvailUserRowsPerPageChange = (event) => {
        setAvailUserRowsPerPage(parseInt(event.target.value, 10));
        setAvailUserPage(0);
    };

    const paginatedAvailableUsers = availableUsers.slice(
        availUserPage * availUserRowsPerPage,
        availUserPage * availUserRowsPerPage + availUserRowsPerPage
    );

    // --- Pagination for Add Role Dialog ---
    const handleAvailRolePageChange = (event, newPage) => {
        setAvailRolePage(newPage);
    };

    const handleAvailRoleRowsPerPageChange = (event) => {
        setAvailRoleRowsPerPage(parseInt(event.target.value, 10));
        setAvailRolePage(0);
    };

    const paginatedAvailableRoles = availableRoles.slice(
        availRolePage * availRoleRowsPerPage,
        availRolePage * availRoleRowsPerPage + availRoleRowsPerPage
    );

    if (loading) {
        return (
            <Container>
                <Typography variant="body1">Loading...</Typography>
            </Container>
        );
    }

    return (
        <Container>
            {/* Header with Back and Save icons */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <Tooltip title="Back to Group List">
                        <IconButton color="primary" onClick={() => navigate('/security/groups')}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" ml={1}>Group Detail</Typography>
                </Box>
                <Tooltip title="Save Changes">
                    <IconButton color="primary" onClick={handleSaveGroup}>
                        <SaveIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Basic Information */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Group Name"
                            value={groupDetail.name}
                            onChange={e => setGroupDetail({ ...groupDetail, name: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            value={groupDetail.description}
                            onChange={e => setGroupDetail({ ...groupDetail, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Grid>
                    {saveMessage && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="primary">{saveMessage}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Group Users Section */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle1" gutterBottom>Group Users</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <TextField
                        label="Search Users"
                        variant="outlined"
                        size="small"
                        value={userFilter}
                        onChange={e => { setUserFilter(e.target.value); setUserPage(0); }}
                    />
                    <Tooltip title="Add User">
                        <IconButton color="primary" onClick={handleOpenAddUserDialog}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupDetail.users && groupDetail.users.length > 0 ? (
                            groupDetail.users.map(user => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Remove User">
                                            <IconButton size="small" onClick={() => handleRemoveUser(user.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2}>No users in this group.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={groupDetail.users ? groupDetail.users.length : 0}
                    page={userPage}
                    onPageChange={handleUserPageChange}
                    rowsPerPage={userRowsPerPage}
                    onRowsPerPageChange={handleUserRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Group Roles Section */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle1" gutterBottom>Group Roles</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <TextField
                        label="Search Roles"
                        variant="outlined"
                        size="small"
                        value={roleSearch}
                        onChange={e => { setRoleSearch(e.target.value); setRolePage(0); }}
                    />
                    <Tooltip title="Add Role">
                        <IconButton color="primary" onClick={handleOpenAddRoleDialog}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Role Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupDetail.roles && groupDetail.roles.length > 0 ? (
                            groupDetail.roles.map(role => (
                                <TableRow key={role.id}>
                                    <TableCell>{role.name}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Remove Role">
                                            <IconButton size="small" onClick={() => handleRemoveRole(role.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2}>No roles assigned to this group.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={groupDetail.roles ? groupDetail.roles.length : 0}
                    page={rolePage}
                    onPageChange={handleRolePageChange}
                    rowsPerPage={roleRowsPerPage}
                    onRowsPerPageChange={handleRoleRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Feature Access Section */}
            <Paper style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle1" gutterBottom>Feature Access</Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Feature Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupDetail.features && groupDetail.features.length > 0 ? (
                            groupDetail.features.map((feature, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{feature.name}</TableCell>
                                    <TableCell>{feature.description}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2}>No feature access assigned.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* Add User Dialog with Pagination */}
            <Dialog open={addUserDialogOpen} onClose={handleCloseAddUserDialog} fullWidth maxWidth="md">
                <DialogTitle>Add User to Group</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Search Available Users"
                        variant="outlined"
                        fullWidth
                        value={userSearch}
                        onChange={e => {
                            setUserSearch(e.target.value);
                            setAvailUserPage(0);
                            fetchAvailableUsers(e.target.value);
                        }}
                    />
                    <Paper style={{ maxHeight: 300, overflow: 'auto', marginTop: 16 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAvailableUsers.map(au => (
                                    <TableRow key={au.id}>
                                        <TableCell>{au.email}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Add User">
                                                <IconButton size="small" onClick={() => handleAddUser(au.id)}>
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <TablePagination
                        component="div"
                        count={availableUsers.length}
                        page={availUserPage}
                        onPageChange={(e, newPage) => setAvailUserPage(newPage)}
                        rowsPerPage={availUserRowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setAvailUserRowsPerPage(parseInt(e.target.value, 10));
                            setAvailUserPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddUserDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Add Role Dialog with Pagination */}
            <Dialog open={addRoleDialogOpen} onClose={handleCloseAddRoleDialog} fullWidth maxWidth="md">
                <DialogTitle>Add Role to Group</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Search Available Roles"
                        variant="outlined"
                        fullWidth
                        value={roleSearch}
                        onChange={e => {
                            setRoleSearch(e.target.value);
                            setAvailRolePage(0);
                            fetchAvailableRoles(e.target.value);
                        }}
                    />
                    <Paper style={{ maxHeight: 300, overflow: 'auto', marginTop: 16 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Role Name</TableCell>
                                    <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAvailableRoles.map(ar => (
                                    <TableRow key={ar.id}>
                                        <TableCell>{ar.name}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Add Role">
                                                <IconButton size="small" onClick={() => handleAddRole(ar.id)}>
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <TablePagination
                        component="div"
                        count={availableRoles.length}
                        page={availRolePage}
                        onPageChange={handleAvailRolePageChange}
                        rowsPerPage={availRoleRowsPerPage}
                        onRowsPerPageChange={handleAvailRoleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddRoleDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
